import { Blueprint } from '../../blueprint/blueprint.class';
import { connectIslands } from '../../../helpers/connect-islands';
import { EmptyIslandDefaults } from '../../../helpers/empty-island-defaults';
import { GALLEY_WALKWAY_PADDING } from '../../../constants';
import { newEmptyIsland } from '../../../helpers/new-empty-island';
import { kitchenShapes, ShapeOption } from '../data';

import { Kitchen } from '../kitchen.class';
import { KitchenShapeBuilderParams } from '../types';

type ShapeBuilder = (kitchen: Kitchen) => void;

const getAllShapeOptions = (): Array<ShapeOption> => {
	return Object.values(kitchenShapes).flatMap((group) => group.options);
};

const findShapeOption = (shapeKey: string): ShapeOption | null => {
	return getAllShapeOptions().find((option) => option.key === shapeKey) ?? null;
};

// --- Individual shape builders ---

const buildGrillIsland: ShapeBuilder = (kitchen) => {
	newEmptyIsland(kitchen, EmptyIslandDefaults.grill, 0, true);
};

const buildBarIsland: ShapeBuilder = (kitchen) => {
	newEmptyIsland(kitchen, EmptyIslandDefaults.bar, 0, true);
};

// 45 is the angle a corner sits at inside every L and U shape: its legs land on the axes, so
// connections, bounding box and dimensions all behave like a normal kitchen. The overhead
// viewport rotates to present it square -- see getOverheadViewRotation in the component package.
const buildCornerIsland: ShapeBuilder = (kitchen) => {
	newEmptyIsland(kitchen, EmptyIslandDefaults.corner, 45, true);
};

const buildPizzaIsland: ShapeBuilder = (kitchen) => {
	newEmptyIsland(kitchen, EmptyIslandDefaults.pizza, 0, true);
};

const buildAsadoIsland: ShapeBuilder = (kitchen) => {
	newEmptyIsland(kitchen, EmptyIslandDefaults.asado, 0, true);
};

const buildGrillBarRight: ShapeBuilder = (kitchen) => {
	const grill = newEmptyIsland(kitchen, EmptyIslandDefaults.grill, 0, true);
	const bar = newEmptyIsland(kitchen, EmptyIslandDefaults.bar, 0);

	connectIslands({
		sourcePanel: bar.frontPanel.getLeftPanel(),
		targetPanel: grill.frontPanel.getRightPanel()
	});
};

const buildBarGrillLeft: ShapeBuilder = (kitchen) => {
	const grill = newEmptyIsland(kitchen, EmptyIslandDefaults.grill, 0, true);
	const bar = newEmptyIsland(kitchen, EmptyIslandDefaults.bar, 0);

	connectIslands({
		sourcePanel: bar.frontPanel.getRightPanel(),
		targetPanel: grill.frontPanel.getLeftPanel()
	});
};

const buildLShapedBarRight: ShapeBuilder = (kitchen) => {
	const grill = newEmptyIsland(kitchen, EmptyIslandDefaults.grill, 0, true);
	const bar = newEmptyIsland(kitchen, EmptyIslandDefaults.bar, 90);

	connectIslands({
		sourcePanel: bar.frontPanel,
		targetPanel: grill.frontPanel.getRightPanel()
	});
};

const buildLShapedBarLeft: ShapeBuilder = (kitchen) => {
	const grill = newEmptyIsland(kitchen, EmptyIslandDefaults.grill, 0, true);
	const bar = newEmptyIsland(kitchen, EmptyIslandDefaults.bar, 270);

	connectIslands({
		sourcePanel: bar.panels.getFrontPanel(),
		targetPanel: grill.panels.getFrontPanel().getLeftPanel()
	});
};

const buildLShapedWithBarRight: ShapeBuilder = (kitchen) => {
	const grill = newEmptyIsland(kitchen, EmptyIslandDefaults.grill, 0, true);
	const corner = newEmptyIsland(kitchen, EmptyIslandDefaults.corner, 45);
	const bar = newEmptyIsland(kitchen, EmptyIslandDefaults.bar, 90);

	connectIslands({
		sourcePanel: corner.frontPanel.getLeftPanel(),
		targetPanel: grill.frontPanel.getRightPanel()
	});

	connectIslands({
		sourcePanel: bar.frontPanel.getLeftPanel(),
		targetPanel: corner.frontPanel.getRightPanel()
	});
};

const buildLShapedWithBarLeft: ShapeBuilder = (kitchen) => {
	const grill = newEmptyIsland(kitchen, EmptyIslandDefaults.grill, 0, true);
	const corner = newEmptyIsland(kitchen, EmptyIslandDefaults.corner, 315);
	const bar = newEmptyIsland(kitchen, EmptyIslandDefaults.bar, 270);

	connectIslands({
		sourcePanel: corner.frontPanel.getRightPanel(),
		targetPanel: grill.frontPanel.getLeftPanel()
	});

	connectIslands({
		sourcePanel: bar.frontPanel.getRightPanel(),
		targetPanel: corner.frontPanel.getLeftPanel()
	});
};

const buildUShaped: ShapeBuilder = (kitchen) => {
	const grill = newEmptyIsland(kitchen, EmptyIslandDefaults.grill, 0, true);
	const lBar = newEmptyIsland(kitchen, EmptyIslandDefaults.bar, 270);
	const rBar = newEmptyIsland(kitchen, EmptyIslandDefaults.bar, 90);

	connectIslands({
		sourcePanel: lBar.frontPanel,
		targetPanel: grill.frontPanel.getLeftPanel()
	});

	connectIslands({
		sourcePanel: rBar.frontPanel,
		targetPanel: grill.frontPanel.getRightPanel()
	});
};

const buildUShapedCornerRight: ShapeBuilder = (kitchen) => {
	const grill = newEmptyIsland(kitchen, EmptyIslandDefaults.grill, 0, true);
	const lBar = newEmptyIsland(kitchen, EmptyIslandDefaults.bar, 270);
	const corner = newEmptyIsland(kitchen, EmptyIslandDefaults.corner, 45);
	const rBar = newEmptyIsland(kitchen, EmptyIslandDefaults.bar, 90);

	connectIslands({
		sourcePanel: lBar.frontPanel,
		targetPanel: grill.frontPanel.getLeftPanel()
	});

	connectIslands({
		sourcePanel: corner.frontPanel.getLeftPanel(),
		targetPanel: grill.frontPanel.getRightPanel()
	});

	connectIslands({
		sourcePanel: rBar.frontPanel.getLeftPanel(),
		targetPanel: corner.frontPanel.getRightPanel()
	});
};

const buildUShapedCornerLeft: ShapeBuilder = (kitchen) => {
	const grill = newEmptyIsland(kitchen, EmptyIslandDefaults.grill, 0, true);
	const corner = newEmptyIsland(kitchen, EmptyIslandDefaults.corner, 315);
	const lBar = newEmptyIsland(kitchen, EmptyIslandDefaults.bar, 270);
	const rBar = newEmptyIsland(kitchen, EmptyIslandDefaults.bar, 90);

	connectIslands({
		sourcePanel: corner.frontPanel.getRightPanel(),
		targetPanel: grill.frontPanel.getLeftPanel()
	});

	connectIslands({
		sourcePanel: lBar.frontPanel.getRightPanel(),
		targetPanel: corner.frontPanel.getLeftPanel()
	});

	connectIslands({
		sourcePanel: rBar.frontPanel,
		targetPanel: grill.frontPanel.getRightPanel()
	});
};

const buildUShapedMiddleCorner: ShapeBuilder = (kitchen) => {
	const grill = newEmptyIsland(kitchen, EmptyIslandDefaults.grill, 0, true);
	const lCorner = newEmptyIsland(kitchen, EmptyIslandDefaults.corner, 315);
	const rCorner = newEmptyIsland(kitchen, EmptyIslandDefaults.corner, 45);
	const lBar = newEmptyIsland(kitchen, EmptyIslandDefaults.bar, 270);
	const rBar = newEmptyIsland(kitchen, EmptyIslandDefaults.bar, 90);

	connectIslands({
		sourcePanel: lCorner.frontPanel.getRightPanel(),
		targetPanel: grill.frontPanel.getLeftPanel()
	});

	connectIslands({
		sourcePanel: rCorner.frontPanel.getLeftPanel(),
		targetPanel: grill.frontPanel.getRightPanel()
	});

	connectIslands({
		sourcePanel: lBar.frontPanel.getRightPanel(),
		targetPanel: lCorner.frontPanel.getLeftPanel()
	});

	connectIslands({
		sourcePanel: rBar.frontPanel.getLeftPanel(),
		targetPanel: rCorner.frontPanel.getRightPanel()
	});
};

const buildGalley: ShapeBuilder = (kitchen) => {
	const grill = newEmptyIsland(kitchen, EmptyIslandDefaults.grill, 0, true);
	const bar = newEmptyIsland(kitchen, EmptyIslandDefaults.bar, 180);

	// front face to front face -- the padding is the walkway the customer stands in, not a joint gap
	connectIslands({
		sourcePanel: bar.frontPanel,
		targetPanel: grill.frontPanel,
		padding: GALLEY_WALKWAY_PADDING
	});
};

// --- Shape key to builder mapping ---

const shapeBuilders: Record<string, ShapeBuilder> = {
	'grill-island': buildGrillIsland,
	'bar-island': buildBarIsland,
	'corner-island': buildCornerIsland,
	'pizza-island': buildPizzaIsland,
	'asado-island': buildAsadoIsland,
	'grill-bar-right': buildGrillBarRight,
	'bar-grill-left': buildBarGrillLeft,
	'l-shaped-bar-right': buildLShapedBarRight,
	'l-shaped-bar-left': buildLShapedBarLeft,
	'l-shaped-with-bar-right': buildLShapedWithBarRight,
	'l-shaped-with-bar-left': buildLShapedWithBarLeft,
	'u-shaped': buildUShaped,
	'u-shaped-corner-right': buildUShapedCornerRight,
	'u-shaped-corner-left': buildUShapedCornerLeft,
	'u-shaped-middle-corner': buildUShapedMiddleCorner,
	'galley': buildGalley
};

// --- Public API ---

export const buildKitchenShapeCfn = (kitchen: Kitchen, shapeKey: string): boolean => {
	const option = findShapeOption(shapeKey);
	if (!option) return false;

	const builder = shapeBuilders[shapeKey];
	if (!builder) return false;

	builder(kitchen);
	kitchen.description = option.label;

	return true;
};

export const createBlueprintFromShapeCfn = (shapeKey: string, params: KitchenShapeBuilderParams): Blueprint | null => {
	const { customer, user } = params;

	const option = findShapeOption(shapeKey);
	if (!option) return null;

	const bp = new Blueprint({ userEmail: user, dataIn: { email: customer }});
	const kitchen = bp.kitchens.getActiveVersion();

	const success = buildKitchenShapeCfn(kitchen, shapeKey);
	if (!success) return null;

	return bp;
};

export const getAvailableShapeKeys = (): Array<string> => {
	return getAllShapeOptions().map((option) => option.key);
};
